import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { onDeleteFilesQueryClient } from "../../../views/library/GrowthDriveUtils/DriveUtils";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import TenantAPI from "../../api/TenantAPI";

export type DeleteFileBody =
  | {
      type: "blobName";
      blobName: string;
      moduleAPIName: string;
      fieldAPIName: string; // For when deleting a file from a subitem
      recordID: string;
      subitemID?: string; // For when deleting a file from a subitem
    }
  | {
      type: "fileID";
      fileID: string;
      moduleAPIName: string;
      fieldAPIName: string;
      recordID: string;
      subitemID?: string; // For when deleting a file from a subitem
    };

interface Props {
  body: DeleteFileBody;
  onSuccess?: () => void;
  onError?: () => void;
}

export default function useDeleteFile() {
  const { cPermissions } = useCloudPermissions();
  const tenantID = useAtomValue(tenantIDAtom);
  const queryClient = useQueryClient();

  const deleteFile = useMutation({
    mutationFn: ({ body }: Props) => {
      if (!cPermissions.cloud_delete_record) {
        return Promise.reject(
          "You do not have permission to delete records in this cloud."
        );
      }

      return TenantAPI.deleteFiles(body);
    },
    onSuccess: (_, { onSuccess }) => {
      onDeleteFilesQueryClient({
        queryClient,
        tenantID: tenantID || "",
      });
      newToastEvent("success", "File successfully deleted.");

      callFunctionIfExists(onSuccess);
    },
    onError: (err, { onError, body, onSuccess }) => {
      const errorMessage = getErrorMessage(err) ?? "Unable to delete file.";

      if (errorMessage.response?.status !== 409) {
        onDeleteFilesQueryClient({
          queryClient,
          tenantID: tenantID || "",
        });

        if (!body.recordID || !body.moduleAPIName) {
          newToastEvent("warning", errorMessage);
        } else {
          // The file wasn't deleted but the source was so, it's not an error.
          callFunctionIfExists(onSuccess);
        }
      }

      callFunctionIfExists(onError);
    },
  });

  return {
    deleteRecordFile: deleteFile.mutate,
    deletingRecordFile: deleteFile.isPending,
    deleteRecordFileError: deleteFile.error
      ? getErrorMessage(deleteFile.error)
      : null,
    reset: () => deleteFile.reset(),
  };
}
